/**
 * React hook that manages the lifecycle of an SSE streaming chat
 * session with the Agent Builder API.
 */

import { useCallback, useRef, useState } from 'react';
import { sendMessageStreaming, startConversationStreaming } from '@/services/agent-stream.service';
import type {
  AgentChatResponse,
  MfeDetectedEventData,
  StreamCompletedEventData,
  StreamErrorEventData,
  StreamingEvent,
  StreamingState,
  StreamingToolCall,
  TextDeltaEventData,
  ThinkingEventData,
  ToolCallCompletedEventData,
  ToolCallErrorEventData,
  ToolCallStartedEventData,
} from '@/types/streaming.types';
import { INITIAL_STREAMING_STATE } from '@/types/streaming.types';

// ============================================================================
// Public types
// ============================================================================

export interface StreamingChatResult {
  readonly conversationId: string | undefined;
  readonly response: AgentChatResponse | undefined;
}

export interface UseStreamingChatOptions {
  readonly onEvent?: (event: StreamingEvent) => void;
  readonly onError?: (error: Error) => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useStreamingChat(options: UseStreamingChatOptions = {}): UseStreamingChatReturn {
  const [streamingState, setStreamingState] = useState<StreamingState>(INITIAL_STREAMING_STATE);
  const abortControllerRef = useRef<AbortController | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const resetStreamingState = useCallback((): void => {
    setStreamingState(INITIAL_STREAMING_STATE);
  }, []);

  const cancelStreaming = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStreamingState((prev) => ({ ...prev, isStreaming: false }));
  }, []);

  const processStreamingEvent = useCallback((event: StreamingEvent): void => {
    optionsRef.current.onEvent?.(event);

    switch (event.type) {
      case 'StreamStarted':
        setStreamingState((prev) => ({
          ...prev,
          conversationId: event.conversation_id,
        }));
        break;

      case 'Thinking':
        applyThinkingEvent(event.data as ThinkingEventData);
        break;

      case 'TextDelta':
        applyTextDeltaEvent(event.data as TextDeltaEventData);
        break;

      case 'TextComplete':
        setStreamingState((prev) => ({ ...prev, thinkingMessage: null }));
        break;

      case 'ToolCallStarted':
        applyToolCallStartedEvent(event.data as ToolCallStartedEventData);
        break;

      case 'ToolCallCompleted':
        applyToolCallCompletedEvent(event.data as ToolCallCompletedEventData);
        break;

      case 'ToolCallError':
        applyToolCallErrorEvent(event.data as ToolCallErrorEventData);
        break;

      case 'MfeDetected':
        applyMfeDetectedEvent(event.data as MfeDetectedEventData);
        break;

      case 'StreamError':
        applyStreamErrorEvent(event.data as StreamErrorEventData);
        break;

      case 'StreamCompleted':
        break;

      default:
        break;
    }
  }, []);

  function applyThinkingEvent(data: ThinkingEventData): void {
    setStreamingState((prev) => ({
      ...prev,
      thinkingMessage: data.message ?? 'Processing...',
    }));
  }

  function applyTextDeltaEvent(data: TextDeltaEventData): void {
    setStreamingState((prev) => ({
      ...prev,
      currentText: data.accumulated_text,
      thinkingMessage: null,
    }));
  }

  function applyToolCallStartedEvent(data: ToolCallStartedEventData): void {
    const newToolCall: StreamingToolCall = {
      tool_name: data.tool_name,
      call_id: data.call_id,
      status: 'running',
    };
    setStreamingState((prev) => ({
      ...prev,
      toolCalls: [...prev.toolCalls, newToolCall],
      thinkingMessage: null,
    }));
  }

  function applyToolCallCompletedEvent(data: ToolCallCompletedEventData): void {
    setStreamingState((prev) => ({
      ...prev,
      toolCalls: prev.toolCalls.map((tc) =>
        tc.call_id === data.call_id
          ? {
              ...tc,
              status: 'completed' as const,
              result: data.result,
              duration_ms: data.duration_ms,
            }
          : tc,
      ),
    }));
  }

  function applyToolCallErrorEvent(data: ToolCallErrorEventData): void {
    setStreamingState((prev) => ({
      ...prev,
      toolCalls: prev.toolCalls.map((tc) =>
        tc.call_id === data.call_id
          ? {
              ...tc,
              status: 'error' as const,
              error_message: data.error_message,
            }
          : tc,
      ),
    }));
  }

  function applyMfeDetectedEvent(data: MfeDetectedEventData): void {
    setStreamingState((prev) => ({
      ...prev,
      mfe: data.mfe,
    }));
  }

  function applyStreamErrorEvent(data: StreamErrorEventData): void {
    setStreamingState((prev) => ({
      ...prev,
      error: data,
      isStreaming: false,
    }));
  }

  const startConversation = useCallback(
    async (agentId: string, message: string): Promise<StreamingChatResult> => {
      cancelStreaming();
      abortControllerRef.current = new AbortController();

      setStreamingState({ ...INITIAL_STREAMING_STATE, isStreaming: true });

      let conversationId: string | undefined;
      let finalResponse: AgentChatResponse | undefined;

      try {
        const stream = startConversationStreaming({ agent_id: agentId, message }, abortControllerRef.current.signal);

        for await (const event of stream) {
          conversationId = event.conversation_id ?? conversationId;
          processStreamingEvent(event);

          if (event.type === 'StreamCompleted') {
            finalResponse = (event.data as StreamCompletedEventData).response;
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Cancelled by user
        } else {
          optionsRef.current.onError?.(error instanceof Error ? error : new Error(String(error)));
          setStreamingState((prev) => ({
            ...prev,
            error: {
              error_code: 'STREAM_ERROR',
              error_message: error instanceof Error ? error.message : String(error),
              is_recoverable: false,
            },
          }));
        }
      } finally {
        setStreamingState((prev) => ({ ...prev, isStreaming: false }));
        abortControllerRef.current = null;
      }

      return { conversationId, response: finalResponse };
    },
    [cancelStreaming, processStreamingEvent],
  );

  const continueConversation = useCallback(
    async (conversationId: string, agentId: string, message: string): Promise<StreamingChatResult> => {
      cancelStreaming();
      abortControllerRef.current = new AbortController();

      setStreamingState({
        ...INITIAL_STREAMING_STATE,
        isStreaming: true,
        conversationId,
      });

      let finalResponse: AgentChatResponse | undefined;

      try {
        const stream = sendMessageStreaming(
          conversationId,
          { agent_id: agentId, message },
          abortControllerRef.current.signal,
        );

        for await (const event of stream) {
          processStreamingEvent(event);

          if (event.type === 'StreamCompleted') {
            finalResponse = (event.data as StreamCompletedEventData).response;
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Cancelled by user
        } else {
          optionsRef.current.onError?.(error instanceof Error ? error : new Error(String(error)));
          setStreamingState((prev) => ({
            ...prev,
            error: {
              error_code: 'STREAM_ERROR',
              error_message: error instanceof Error ? error.message : String(error),
              is_recoverable: false,
            },
          }));
        }
      } finally {
        setStreamingState((prev) => ({ ...prev, isStreaming: false }));
        abortControllerRef.current = null;
      }

      return { conversationId, response: finalResponse };
    },
    [cancelStreaming, processStreamingEvent],
  );

  return {
    streamingState,
    startConversation,
    continueConversation,
    cancelStreaming,
    resetStreamingState,
  };
}

// ============================================================================
// Return type
// ============================================================================

export interface UseStreamingChatReturn {
  readonly streamingState: StreamingState;
  readonly startConversation: (agentId: string, message: string) => Promise<StreamingChatResult>;
  readonly continueConversation: (
    conversationId: string,
    agentId: string,
    message: string,
  ) => Promise<StreamingChatResult>;
  readonly cancelStreaming: () => void;
  readonly resetStreamingState: () => void;
}
